import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HistoryIcon from '@mui/icons-material/History'
import axios from 'axios'
import { API_BASE_URL } from '../../constants/system'
import { DashboardSignOff, DashboardSignOffRevoked } from '../../types/gotypes'

// Mirrors goapi/pkg/server/dashboard_signoff_history.go's
// DashboardSignOffHistoryEntry (defined here rather than gotypes.ts, same as
// DashboardEditHistoryPanel's DashboardEditHistoryEntry).
export interface DashboardSignOffHistoryEntry {
  id: string
  created_at: number
  user_email: string
  scope: 'dashboard' | 'card'
  multi_table_ids?: string[]
}

interface DashboardSignOffHistoryPanelProps {
  active: boolean
  customer: string
  dashboardId: string
  // Current dashboard-level sign-off state from the config — shown separately
  // because a dashboard sign-off with zero Gen AI cards writes no durable
  // event rows (see the Go handler's comment).
  currentSignOff?: DashboardSignOff
  signOffRevoked?: DashboardSignOffRevoked
  // multi_table_id -> widget title, so card-level events name the card the
  // way the dashboard does rather than showing a bare id.
  widgetTitles?: Record<string, string>
}

const formatTime = (epochSeconds: number) => {
  const ms = epochSeconds > 1e12 ? epochSeconds : epochSeconds * 1000
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/** The Sign-off tab of the dashboard History panel (#974 H1): who signed off
 *  what and when. Events come from the durable card-snapshot records that
 *  card and dashboard sign-offs write (#963); the current dashboard-level
 *  state is shown on top. `active` gates the fetch, same as the Text edits
 *  tab. Rendered by both History surfaces via DashboardHistoryTabs (#974). */
const DashboardSignOffHistoryPanel: React.FC<DashboardSignOffHistoryPanelProps> = ({
  active,
  customer,
  dashboardId,
  currentSignOff,
  signOffRevoked,
  widgetTitles = {},
}) => {
  const [entries, setEntries] = useState<DashboardSignOffHistoryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active || !customer || !dashboardId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    axios
      .get<{ entries?: DashboardSignOffHistoryEntry[] }>(
        `${API_BASE_URL}/dashboards/${customer}/${dashboardId}/sign-off-history`
      )
      .then(res => {
        if (cancelled) return
        setEntries(res.data.entries || [])
      })
      .catch(err => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load sign-off history')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [active, customer, dashboardId])

  const cardNames = (ids?: string[]) =>
    (ids || []).map(id => widgetTitles[id] || id)

  return (
    <Box>
      {/* Current dashboard-level state */}
      {currentSignOff && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
          <Typography variant="body2" color="success.main">
            Signed off by {currentSignOff.user_email} · {formatTime(currentSignOff.signed_at)}
          </Typography>
        </Box>
      )}
      {!currentSignOff && signOffRevoked && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <HistoryIcon sx={{ fontSize: 18, color: 'warning.main' }} />
          <Typography variant="body2" color="warning.main">
            Sign-off{signOffRevoked.previous_user ? ` by ${signOffRevoked.previous_user}` : ''} was revoked
            by {signOffRevoked.revoked_by}'s edit · {formatTime(signOffRevoked.revoked_at)}
          </Typography>
        </Box>
      )}
      {!currentSignOff && !signOffRevoked && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          This dashboard is not currently signed off.
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && entries.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          No sign-off events recorded yet.
        </Typography>
      )}
      {!loading && !error && entries.map(entry => (
        <Box
          key={entry.id}
          sx={{
            py: 1.25,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Signed off by {entry.user_email || '(unknown)'} · {formatTime(entry.created_at)}
            </Typography>
            <Chip
              label={entry.scope === 'dashboard' ? 'whole dashboard' : 'card'}
              size="small"
              color={entry.scope === 'dashboard' ? 'success' : 'default'}
            />
          </Box>
          {entry.scope === 'card' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Card: {cardNames(entry.multi_table_ids).join(', ')}
            </Typography>
          )}
          {entry.scope === 'dashboard' && (entry.multi_table_ids?.length || 0) > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Cards captured: {cardNames(entry.multi_table_ids).join(', ')}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  )
}

export default DashboardSignOffHistoryPanel
